import { i18n, type Messages } from "@lingui/core";

import { location } from "./app/location";
import { languageOf } from "./languages";

// The ?lingui query tells the Lingui Vite plugin to compile the JSON catalogs
const localeModules = import.meta.glob<{ messages: Messages }>(
  "./locales/*.json",
  { query: "?lingui" },
);

export const locales = Object.keys(localeModules).flatMap((path) => {
  const match = path.match(/\.\/locales\/(.+)\.json/);
  return match ? [match[1]] : [];
});

const defaultLocale = "en";

const LOCALE_STORAGE_KEY = "seerlinq.locale";

/**
 * Activates the closest catalog: the requested tag, then its language (the
 * default variant: `ar-SA` → `ar`), then English.
 */
export async function setLocale(locale: string) {
  const resolved =
    [locale, languageOf(locale)].find((candidate) =>
      locales.includes(candidate),
    ) ?? defaultLocale;

  const loader = localeModules[`./locales/${resolved}.json`];
  if (!loader) return;

  const { messages } = await loader();
  i18n.load(resolved, messages);
  i18n.activate(resolved);
  document.documentElement.lang = resolved;
}

/** Remember the user's choice so it survives reloads and future sessions. */
export function persistLocale(locale: string) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable (private mode, disabled cookies) — the choice
    // just won't persist, which is acceptable.
  }
}

export function getInitialLocale(): string {
  // An explicit `?locale=` wins (shareable/testing links); otherwise fall back
  // to the persisted choice, then the default. `setLocale` guards unknown
  // values, so a stale stored code can't break startup.
  const fromQuery = new URL(location.href).searchParams.get("locale");
  if (fromQuery) return fromQuery;

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Ignore storage errors and use the default.
  }

  return defaultLocale;
}
